import React, { useEffect, useRef, useState } from 'react';
import cc from 'classnames';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AxiosStatus } from 'api/axiosConfig';
import api from 'api/api';
import { IApiResponse } from 'utils/models';
import LoaderPage from 'ui/LoaderPage/LoaderPage';
import ErrorPage from 'ui/ErrorPage/ErrorPage';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import TablePagination from 'ui/TableInputs/TablePagination/TablePagination';
import LoaderForTable from 'ui/LoaderForTable/LoaderForTable';
import { amountFormatter } from 'utils/formatters';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import WalletClick from 'ui/TableInputs/WalletEvent/WalletEvent';
import NavigateBackBtn from 'ui/NavigateBackBtn/NavigateBackBtn';
import { IRecallMobileMoney, IApiRecallBulkMM } from '../../RecallMobileMoney.model';

interface IPendingBulkDetails {
  isOpen: boolean;
  data: IRecallMobileMoney;
  status: string;
  onCloseTable: () => void;
}

const RecallBulkMMDetails = ({ isOpen, data, status, onCloseTable }: IPendingBulkDetails) => {
  const [loadingStatus, setLoadingStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const pendingLoading = loadingStatus === AxiosStatus.PENDING;
  const successLoaded = loadingStatus === AxiosStatus.SUCCESS;
  const failureLoaded = loadingStatus === AxiosStatus.FAILURE;
  const gridRef = useRef<AgGridReact<IRecallMobileMoney>>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [rowData, setRowData] = useState<IRecallMobileMoney[]>([]);
  // Requesting Data for Table
  const requestDataForTable = () => {
    gridRef.current?.api?.showLoadingOverlay();
    const requestBody = {
      bulkId: data.bulkId,
      pageSize,
      pageNumber,
    };
    api
      .post('transfer/transactionDrillDownByBulkId', requestBody)
      .then((datas: IApiRecallBulkMM) => {
        const formattedData = JSON.parse(datas.transaction);
        setRowData(formattedData);
        setTotalRows(formattedData.length);
      })
      .catch((error: IApiResponse) => {
        console.error(error);
        setLoadingStatus(AxiosStatus.FAILURE);
        gridRef.current?.api.hideOverlay();
      });
  };
  useEffect(requestDataForTable, [data.bulkId, pageNumber, pageSize]);

  // TODO fix double request for first render to display NoData component
  useEffect(() => {
    setLoadingStatus(AxiosStatus.PENDING);
    const requestBody = {
      bulkId: data.bulkId,
      pageSize,
      pageNumber,
    };
    api
      .post('transfer/transactionDrillDownByBulkId', requestBody)
      .then((datas: IApiRecallBulkMM) => {
        const formattedData = JSON.parse(datas.transaction);
        setRowData(formattedData);
        setTotalRows(formattedData.length);
        setLoadingStatus(AxiosStatus.SUCCESS);
      })
      .catch((error: IApiResponse) => {
        setLoadingStatus(AxiosStatus.FAILURE);
        console.error(error);
      });
  }, [data.bulkId, pageNumber, pageSize]);

  const renderElevy = (prop: ICellRendererParams<IRecallMobileMoney>) => {
    if (prop.data && prop.data?.eleveyFee) {
      return <div>{amountFormatter('ghs', false)(String(prop.data?.eleveyFee))}</div>;
    }
    return null;
  };

  const renderAmount = (prop: ICellRendererParams<IRecallMobileMoney>) => {
    if (prop.data && prop.data?.amount) {
      return <div>{amountFormatter('ghs', false)(String(prop.data?.amount))}</div>;
    }
    return null;
  };

  const renderNarration = (prop: ICellRendererParams<IRecallMobileMoney>) => {
    if (prop.data) {
      return <div>{prop.data?.description}</div>;
    }
    return null;
  };

  const renderTotalAmount = (prop: ICellRendererParams<IRecallMobileMoney>) => {
    if (prop.data && prop.data?.amount && prop.data?.eleveyFee) {
      const totalAmount = Number(prop.data?.amount) + Number(prop.data?.eleveyFee);
      return <div>{amountFormatter('ghs', false)(String(totalAmount))}</div>;
    }
    return null;
  };
  const renderBeneficiaryName = (prop: ICellRendererParams<IRecallMobileMoney>) => {
    if (prop.data && prop.data?.beneficiaryName) {
      return <div>{prop.data.beneficiaryName}</div>;
    }
    return null;
  };

  const renderWallet = (prop: ICellRendererParams<IRecallMobileMoney>) => {
    if (prop.data) {
      return <WalletClick walletName={prop.data?.walletName} walletNumber={prop.data?.beneficiaryWalletNumber} />;
    }
    return null;
  };

  const renderNetwork = (prop: ICellRendererParams<IRecallMobileMoney>) => {
    if (prop.data && prop.data?.netWork) {
      return <div>{prop.data.netWork}</div>;
    }
    return null;
  };

  const renderStatus = (prop: ICellRendererParams<IRecallMobileMoney>) => {
    if (prop.data) {
      return <div>{prop.data ? status : ''}</div>;
    }
    return null;
  };
  const columnDefs: ColDef[] = [
    {
      field: 'beneficiaryName',
      headerName: 'Beneficiary Name',
      flex: 1,
      minWidth: 150,
      cellRenderer: renderBeneficiaryName,
    },
    {
      field: 'wallet',
      headerName: 'Wallet',
      flex: 1,
      minWidth: 150,
      cellRenderer: renderWallet,
    },
    {
      field: 'network',
      headerName: 'Network',
      flex: 1,
      minWidth: 150,
      cellRenderer: renderNetwork,
    },
    { field: 'amount', headerName: 'Amount', flex: 1, minWidth: 130, cellRenderer: renderAmount },
    { field: 'elevy', headerName: 'E-Levy', flex: 1, minWidth: 130, cellRenderer: renderElevy },
    { field: 'totalAmount', headerName: 'Total Amount', flex: 1, minWidth: 150, cellRenderer: renderTotalAmount },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 150, cellRenderer: renderNarration },
    { field: 'status', headerName: 'Status', width: 220, cellRenderer: renderStatus },
  ];

  // Pagination & Filters handlers
  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPageNumber(newPage);
    requestDataForTable();
  };

  const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newPageSize = parseInt(event.target.value as string, 10);
    setPageSize(newPageSize);
    setPageNumber(1);
  };

  return (
    <>
      {pendingLoading && <LoaderPage />}
      {failureLoaded && <ErrorPage />}
      {successLoaded && isOpen && (
        <>
          {/* <pre>{JSON.stringify(rowData, null, 2)}</pre> */}
          <DashboardCard>
            <NavigateBackBtn onBackBtnClick={onCloseTable}>Back</NavigateBackBtn>
            <NavigationTitle to="#/" layoutType={LayoutType.dashboard} onClick={onCloseTable}>
              Bulk Transfers List
            </NavigationTitle>
            <div className={cc('tableWrapper', 'ag-theme-material')}>
              <AgGridReact
                ref={gridRef}
                rowData={rowData}
                loadingOverlayComponent={LoaderForTable}
                columnDefs={columnDefs}
                defaultColDef={{ resizable: true }}
                suppressCellFocus
                enableCellTextSelection
                headerHeight={60}
                rowHeight={70}
                undoRedoCellEditing
                suppressRowHoverHighlight
                pagination
                paginationPageSize={pageSize}
                suppressPaginationPanel
                suppressScrollOnNewData
                domLayout="autoHeight"
              />
            </div>
            <TablePagination
              count={Math.ceil(totalRows / pageSize)}
              page={pageNumber}
              pageSize={pageSize}
              totalCount={totalRows}
              handlePageChange={handlePageChange}
              handlePageSizeChange={handlePageSizeChange}
            />
          </DashboardCard>
        </>
      )}
    </>
  );
};

export default RecallBulkMMDetails;
